import { forwardRef, useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Slide, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";

import RetailerContext from "../../../context";
import { AwardsContext } from "../../../context/AwardsContext";

import ContestArt from '../../../shared/img/Bitmap.png';

import { getTranslation } from "../../../utils/translate";

import Wysiwyg from "../../Wysiwyg";
import { DialogContestTitle } from "../DialogContestTitle";

import { useFetchAchievements } from "../../../hooks/useFetchAchievements";

const Transition = forwardRef(function Transition(props, ref) {
    return (
        <Slide
            direction="up"
            ref={ref}
            timeout={10000}
            {...props}
        />
    );
});

const AwardDialogoxBox = ({ open, onClose }) => {
    const { t } = useTranslation(['gamification']);
    const history = useHistory();

    const { code: languageCode } = useSelector(state => state.language);

    const [enteredContentStatus, setEnteredContentStatus] = useState('');
    const [isThereAnotherAchievement, setIsThereAnotherAchievement] = useState('idle');

    const { retailer } = useContext(RetailerContext);
    const { achievement, setModalToOpen } = useContext(AwardsContext);

    const contests = useSelector(state => state.contests);
    const contest = contests?.find(item => item?.id) || null;

    let featuredImage;

    if (contest) {
        const translatedContest = getTranslation(contest, { key: 'languages_code', code: languageCode });

        featuredImage = `${process.env.REACT_APP_API_URL}/assets/${translatedContest?.featured_image}?key=contest-feature-image` || '';
    }

    let translatedAchievement = {};

    if (achievement) {
        translatedAchievement = getTranslation(achievement, { key: 'languages_code', code: languageCode });
    }

    const filter = {
        _and: [
            {
                status: {
                    '_eq': 'published'
                }
            },
            ...(contest ? [
                {
                    contests: {
                        contests_id: {
                            '_eq': contest.id
                        }
                    }
                }
            ] : []),
        ]
    };

    const { data } = useFetchAchievements(filter);

    let nextAchievement = null;
    let total = 0;
    let completed = 0;

    for (let i = 0; i < data?.length; i++) {
        const achievement = data[i];

        if (achievement.type !== 'contest-form') {
            total += 1;
        }

        // if achievement has not been completed, then set it as next achievement
        if (((achievement.points / achievement.target) * 100) >= 100) {
            if (achievement.type !== 'contest-form') {
                completed += 1;
            }
        } else {
            // if contest-form achievement is not completed, prioritize it
            if (achievement.type === 'contest-form') {
                nextAchievement = achievement; // Always set contest-form as next if incomplete
            } else if (!nextAchievement) {
                // Only set non-contest-form achievement if no nextAchievement is set yet
                nextAchievement = nextAchievement || achievement;
            }
        }
    }

    const progressPercentage = Math.floor((completed / total) * 100);

    let buttonText = null;

    if (completed === 0) {
        buttonText = t('firstChanceToWin');
    } else if (nextAchievement?.type === 'contest-form') {
        buttonText = t('enterContest');
    } else if (completed === total) {
        buttonText = t('completedAllAchievements');
    } else {
        buttonText = t('anotherChanceToWin');
    }


    const handleClose = (close = false) => {
        let modalToOpen = null;

        // if user has not entered contest, then open contest form
        if (enteredContentStatus === 'not entered') {
            modalToOpen = 'contest-info';
        }

        if (isThereAnotherAchievement === 'yes') {
            if (achievement.modalToOpen) {
                modalToOpen = achievement.modalToOpen;
            }
        } else {
            modalToOpen = 'promotion';
        }

        // if close is true, then close the modal
        if (close === true) {
            modalToOpen = 'promotion';
        }

        onClose(modalToOpen);
    };

    const handleOnClose = (close = false) => {
        let modalToOpen = '';

        // if user has completed all achievements, then open promotion
        if (!nextAchievement) {
            modalToOpen = 'promotion';
        } else {
            // if next achievement is a quiz, redirect to beef cut page
            if (nextAchievement?.type === 'quiz') {
                const beefProduct = nextAchievement?.beef_product?.[0];
                if (beefProduct) {
                    setModalToOpen(null);
                    // Redirect to beef cut page using React Router
                    history.push(`/beef/${beefProduct.slug || beefProduct.id}`);
                    return;
                }
            }
            modalToOpen = nextAchievement.id;
        }

        // if close is true, then close the modal
        if (close === true) {
            modalToOpen = 'promotion';
        }
        
        onClose(modalToOpen, nextAchievement || undefined);
    };

    return (
        <Dialog
            className='page-header'
            fullWidth={true}
            maxWidth={'xs'}
            open={open}
            onClose={() => handleClose(true)}
            TransitionComponent={Transition}
            scroll="paper"
            PaperProps={{
                sx: {
                    overflow: 'visible',
                    borderRadius: '1rem',
                    marginTop: '5rem',
                }
            }}
        >
            <IconButton
                onClick={() => handleClose('promotion')}
                sx={{
                    color: '#000',
                    position: 'absolute',
                    top: '0.15rem',
                    right: '0.25rem',
                    zIndex: '1',
                }}
            >
                <Close fontSize="inherit" />
            </IconButton>

            <DialogTitle>
                <DialogContestTitle />
            </DialogTitle>

            <DialogContent
                sx={{}}
            >
                <Box
                    sx={{
                        width: '100%',
                        background: "#eeeeee",
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: `${progressPercentage}%`,
                            background: progressPercentage === 0 ? 'transparent' : "#004466",
                            clear: "none",
                            zIndex:"-1",
                            position: "relative-layer",
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: progressPercentage === 0 ? '#000' : '#ffffff',
                            borderRadius: '0.5rem',
                            transition: 'width 600ms ease',
                            willChange: 'width',
                            fontSize: '1rem',
                            px: 0.5,
                        }}
                    >
                        {progressPercentage}%
                    </Box>
                </Box>

                <Typography
                    sx={{
                        textAlign: 'center',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',

                    }}
                >
                    {completed} of {total} Entries
                </Typography>

                <Typography
                    className='title'
                    variant="h2"
                    sx={{
                        color: '#000',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        px: 1,
                        my: 1,
                        '& p': {
                            m: 0,
                        },
                    }}
                >
                    {completed !== total && <Wysiwyg allowedTags={['strong']}>{translatedAchievement?.success_message || t('contestGainedAnotherEntry')}</Wysiwyg>}
                    {completed === total && <Wysiwyg allowedTags={['strong']}>{t('completedAllAchievementsContent')}</Wysiwyg>}
                </Typography>

                <Box
                    sx={{
                        height: '400px',
                        width: '100%',
                    }}
                >
                    <img
                        src={featuredImage || ContestArt}
                        alt={'Contest Art'}
                        style={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center',
                        }}
                    />
                </Box>
                
                <Button
                    variant="contained"
                    sx={{
                        borderRadius: '0',
                        backgroundColor: '#008F01',
                        fontFamily: retailer.button_font,
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        width: '100%',
                        mb: 2,
                    }}
                    onClick={handleOnClose}
                >
                    {buttonText}
                </Button>
            </DialogContent>
        </Dialog>
    )
}

const DialogAward = ({ defaultOpen = false, onClose }) => {
    const { achievement } = useContext(AwardsContext);

    const handleClose = (component, item) => {
        if (onClose) {
            onClose(component, item);
        }
    };

    return (
        <Box
            className='no-print'
        >
            <AwardDialogoxBox
                achievement={achievement}
                open={defaultOpen}
                onClose={handleClose}
                key={defaultOpen}
            />
        </Box>
    )
}

export default DialogAward;